import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  Area,
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Plus, LineChart as LineIcon } from "lucide-react";

const BUREAU_COLORS = {
  equifax: "#FF6B4A",
  experian: "#4B90FF",
  transunion: "#7CFF9B",
  vantage: "#A8ADA5",
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0B0C0E] border border-[#26282C] rounded-sm p-3 text-xs shadow-[0_0_30px_rgba(213,255,63,0.15)]">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C] mb-1">
        {label}
      </div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="ticker text-sm" style={{ color: p.color }}>
            {p.value}
          </span>
          <span className="text-[10px] font-mono text-[#A8ADA5]">{p.name}</span>
        </div>
      ))}
    </div>
  );
}

export function ScoreHistoryCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    score: "",
    bureau: "experian",
    logged_on: new Date().toISOString().slice(0, 10),
    source: "",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/score/history");
      setData(r.data);
    } catch (err) {
      console.warn("score history", err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/score/log", {
        score: parseInt(form.score, 10),
        bureau: form.bureau,
        logged_on: form.logged_on,
        source: form.source || null,
        note: form.note || null,
      });
      toast.success("Score logged");
      setOpen(false);
      setForm({ ...form, score: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const entries = data?.entries || [];
  const projection = data?.projection || [];

  // Build chart data — merge entries and projection by date, per-bureau series
  const dateSet = new Set([...entries.map((e) => e.logged_on), ...projection.map((p) => p.date)]);
  const dates = [...dateSet].sort();
  const chartData = dates.map((d) => {
    const row = { date: d };
    for (const e of entries) {
      if (e.logged_on === d) row[e.bureau] = e.score;
    }
    const p = projection.find((x) => x.date === d);
    if (p) {
      row.projected_low = p.low;
      row.projected_high = p.high;
    }
    return row;
  });

  const hasEntries = entries.length > 0;
  const bureauSeen = [...new Set(entries.map((e) => e.bureau))];
  const latest = data?.latest_score;
  const starting = data?.starting_score;
  const delta = latest != null && starting != null ? latest - starting : 0;

  return (
    <div
      className="bg-[#131518] rounded-3xl border border-white/5 hover:border-[#D5FF3F]/20 hover:shadow-[0_0_40px_rgba(213,255,63,0.08)] transition-all duration-500 p-8 mb-6"
      data-testid="score-history-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A8ADA5] mb-2 flex items-center gap-2">
            <LineIcon size={11} className="text-[#D5FF3F]" /> Score history
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-[0.95] tracking-tight">
            {hasEntries ? "Your curve." : "Log your first score."}
          </h2>
          {hasEntries && (
            <p className="text-sm text-[#A8ADA5] mt-2">
              {starting} → {latest}{" "}
              {delta !== 0 && (
                <span
                  className={`ticker ml-1 ${delta > 0 ? "text-[#7CFF9B]" : "text-[#FF6B4A]"}`}
                >
                  ({delta > 0 ? "+" : ""}
                  {delta} pts)
                </span>
              )}
            </p>
          )}
        </div>
        <Button
          onClick={() => setOpen(true)}
          data-testid="log-score-btn"
          className="rounded-full bg-[#D5FF3F] text-[#0B0C0E] hover:bg-[#c4f02e] active:scale-95 transition-all duration-200 h-10 px-5 font-mono text-xs uppercase tracking-[0.15em]"
        >
          <Plus size={13} className="mr-1.5" /> Log score
        </Button>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center text-sm text-[#6D716C] font-mono">
          Loading history…
        </div>
      ) : !hasEntries ? (
        <div className="rounded-2xl border border-dashed border-[#26282C] p-10 text-center">
          <TrendingUp size={26} className="text-[#6D716C] mx-auto mb-3" />
          <div className="text-sm text-[#A8ADA5] max-w-md mx-auto leading-relaxed">
            Log your FICO / Vantage score each month and Aura overlays your{" "}
            <span className="text-[#F5F3EE]">actual curve</span> on top of the projected lift from
            every dispute + tradeline. Your before-and-after starts here.
          </div>
        </div>
      ) : (
        <>
          <div className="h-64" data-testid="score-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 5, right: 12, left: -20, bottom: 5 }}>
                <defs>
                  <linearGradient id="projected" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D5FF3F" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#D5FF3F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#26282C" strokeDasharray="2 4" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#6D716C", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  axisLine={{ stroke: "#26282C" }}
                  tickLine={false}
                />
                <YAxis
                  domain={[300, 850]}
                  tick={{ fill: "#6D716C", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  axisLine={{ stroke: "#26282C" }}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#D5FF3F", strokeOpacity: 0.3 }} />
                {data?.target_score && (
                  <ReferenceLine
                    y={data.target_score}
                    stroke="#D5FF3F"
                    strokeDasharray="4 4"
                    label={{
                      value: `Target ${data.target_score}`,
                      fill: "#D5FF3F",
                      fontSize: 10,
                      fontFamily: "JetBrains Mono",
                      position: "insideTopRight",
                    }}
                  />
                )}
                {/* Projection band */}
                <Area
                  type="monotone"
                  dataKey="projected_high"
                  stroke="#D5FF3F"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  fill="url(#projected)"
                  name="Projected"
                  isAnimationActive
                />
                {bureauSeen.map((b) => (
                  <Line
                    key={b}
                    type="monotone"
                    dataKey={b}
                    stroke={BUREAU_COLORS[b] || "#F5F3EE"}
                    strokeWidth={2}
                    dot={{ r: 4, fill: BUREAU_COLORS[b], strokeWidth: 0 }}
                    activeDot={{ r: 6, fill: BUREAU_COLORS[b] }}
                    connectNulls
                    name={b.charAt(0).toUpperCase() + b.slice(1)}
                    isAnimationActive
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-white/5">
            {bureauSeen.map((b) => (
              <div key={b} className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ background: BUREAU_COLORS[b] || "#F5F3EE" }}
                />
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#A8ADA5]">
                  {b}
                </span>
              </div>
            ))}
            <div className="flex items-center gap-2 ml-auto">
              <div className="w-4 h-0.5 border-t border-dashed border-[#D5FF3F]" />
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#A8ADA5]">
                Projected lift
              </span>
            </div>
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-md bg-[#131518] border-white/10 text-[#F5F3EE]"
          data-testid="log-score-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Log a score.</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A8ADA5]">
                  Score (300-850)
                </Label>
                <Input
                  required
                  type="number"
                  min={300}
                  max={850}
                  value={form.score}
                  onChange={(e) => setForm({ ...form, score: e.target.value })}
                  data-testid="log-score-value"
                  className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-white/10"
                />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A8ADA5]">
                  Bureau
                </Label>
                <select
                  value={form.bureau}
                  onChange={(e) => setForm({ ...form, bureau: e.target.value })}
                  data-testid="log-score-bureau"
                  className="mt-1 w-full rounded-sm bg-[#0B0C0E] border border-white/10 px-3 py-2 font-mono text-sm"
                >
                  <option value="experian">Experian</option>
                  <option value="equifax">Equifax</option>
                  <option value="transunion">TransUnion</option>
                  <option value="vantage">Vantage / other</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A8ADA5]">
                Date pulled
              </Label>
              <Input
                type="date"
                value={form.logged_on}
                onChange={(e) => setForm({ ...form, logged_on: e.target.value })}
                data-testid="log-score-date"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-white/10"
              />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A8ADA5]">
                Source (optional)
              </Label>
              <Input
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                data-testid="log-score-source"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-white/10"
                placeholder="Credit Karma, myFICO…"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-full border border-white/10 bg-transparent hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="log-score-submit"
                className="rounded-full bg-[#D5FF3F] text-[#0B0C0E] hover:bg-[#c4f02e] active:scale-95 h-10 px-5"
              >
                {submitting ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
